import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Wallet, ReceiptText, Trash2 } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { PayoutOutstanding } from './PayoutOutstanding';
import { PayoutVouchers } from './PayoutVouchers';
import { PayoutTrash } from './PayoutTrash';
import { ReferralPayoutReceiptModal } from './ReferralPayoutReceiptModal';

type TabKey = 'outstanding' | 'vouchers' | 'trash';

/**
 * Payouts — the single Finance entry point for money paid out.
 *
 * Deliberately **three flat tabs, one level deep**:
 *   Outstanding → every referral partner we owe (doctors included)
 *   Vouchers    → every issued `RP-` voucher in one ledger
 *   Trash       → removed accruals
 *
 * Doctors earn through the same referral-partner ledger, so there is no
 * separate doctor payout surface (ADR-015).
 */
export function Payouts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess } = usePermissions();
  const [receiptId, setReceiptId] = useState<string | null>(null);

  // Settlement access is still granted through the legacy doctor-settlement
  // permission for existing roles, but the ledger itself is referral-only.
  const canReferrals =
    hasAccess('/billing/referral-payouts') || hasAccess('/billing/revenue-settlements');
  const canManage = canReferrals;
  const canTrash = canManage;

  const tabs = useMemo(() => {
    const list: { key: TabKey; label: string; icon: React.ElementType }[] = [];
    if (canManage) list.push({ key: 'outstanding', label: 'Outstanding', icon: Wallet });
    if (canManage) list.push({ key: 'vouchers', label: 'Vouchers', icon: ReceiptText });
    if (canTrash) list.push({ key: 'trash', label: 'Trash', icon: Trash2 });
    return list;
  }, [canManage, canTrash]);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requested = params.get('tab') as TabKey | null;
  const active: TabKey | null =
    tabs.find((t) => t.key === requested)?.key ?? tabs[0]?.key ?? null;

  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(location.search);
    next.set('tab', key);
    navigate(`${location.pathname}?${next.toString()}`, { replace: true });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Wallet size={20} className="text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-text">Payouts</h1>
          <p className="text-sm text-text-muted mt-0.5">
            One ledger for every referral partner — doctors, employees and outside agents —
            settle their shares and print numbered payout vouchers.
          </p>
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="flex items-center gap-1 bg-surface border border-border rounded-lg p-1 flex-wrap">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = active === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setTab(tab.key)}
                className={`px-3.5 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  isActive ? 'bg-primary text-white' : 'text-text-muted hover:text-text'
                }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {active === 'outstanding' && canManage && (
        <PayoutOutstanding canDoctors={false} canReferrals={canReferrals} />
      )}
      {active === 'vouchers' && canManage && (
        <PayoutVouchers payeeType="REFERRAL" onViewReceipt={setReceiptId} />
      )}
      {active === 'trash' && canTrash && <PayoutTrash />}

      {tabs.length === 0 && (
        <div className="rounded-xl border border-border bg-surface px-4 py-10 text-center text-sm text-text-muted">
          You do not have access to payouts. Ask an administrator to grant
          <span className="font-medium text-text"> settlement </span>
          permissions.
        </div>
      )}

      {receiptId && (
        <ReferralPayoutReceiptModal payoutId={receiptId} onClose={() => setReceiptId(null)} />
      )}
    </div>
  );
}
